/**
 * BaseAdapter - Interface/Contract for table adapters
 *
 * Any table adapter (AG-Grid, TanStack, etc.) must implement these props:
 *
 * Required Props:
 * - columns: Array of column definitions (already transformed to adapter format)
 * - rows: Array of row data objects
 * - gridRef: Ref to attach to the underlying grid
 *
 * Optional Props:
 * - sorting: { enabled, onSortChange }
 * - pagination: { enabled, pageSize, currentPage, onPageChange }
 * - selection: { mode, onSelectionChange, isRowSelectable }
 * - loading: boolean
 * - rowHeight: number
 * - headerHeight: number
 * - domLayout: 'normal' | 'autoHeight' | 'print'
 * - onRowClick: (row, event) => void
 * - onRowDoubleClick: (row, event) => void
 * - onGridReady: (event) => void
 * - className: string
 * - style: object
 *
 * The adapter is responsible for:
 * 1. Rendering the underlying table library
 * 2. Translating events to the common format
 * 3. Applying styles and themes
 */

export const ADAPTER_PROP_TYPES = {
    // Data
    columns: 'array',
    rows: 'array',
    gridRef: 'object',

    // Features
    sorting: 'object',
    pagination: 'object',
    selection: 'object',

    // State
    loading: 'boolean',

    // Sizing
    rowHeight: 'number',
    headerHeight: 'number',
    domLayout: 'string',
    height: 'number',

    // Events
    onRowClick: 'function',
    onRowDoubleClick: 'function',
    onGridReady: 'function',
    onSortChanged: 'function',
    onSelectionChanged: 'function',

    // Styling
    className: 'string',
    style: 'object',
}

export const DEFAULT_ADAPTER_VALUES = {
    rowHeight: 40,
    headerHeight: 44,
    domLayout: 'normal',
}

/**
 * Validate adapter props (development helper)
 * @param {Object} props - Props to validate
 * @returns {Array} Array of validation errors
 */
export const validateAdapterProps = (props) => {
    const errors = []

    if (!props.columns) {
        errors.push('columns prop is required')
    }

    if (!Array.isArray(props.columns)) {
        errors.push('columns must be an array')
    }

    if (props.rows && !Array.isArray(props.rows)) {
        errors.push('rows must be an array')
    }

    return errors
}
